import { useLayoutEffect, useRef, useState } from "react";
import { observeTextLayout } from "../utils/observeTextLayout";

/** Sub-pixel line heights can make the full height one pixel taller than the clamped height. */
const ROUNDING_PX = 1;

/**
 * The "more"/"less" toggle of a text in the two-line view. It shows only when two lines cannot
 * show the whole text at the current width, on any screen.
 *
 * <p>Collapsed, the text needs the toggle when its full height is taller than the two lines that
 * show. Expanded, "less" stays while the text is taller than those two lines. When a wider column
 * lets two lines hold it, the text collapses and no toggle shows.
 *
 * <p>Measured after layout, after a resize and after web fonts load. An element without height
 * (in a hidden tab, or in jsdom) cannot be measured: the toggle then shows, as it did before this
 * measurement existed.
 */
export function useTwoLineToggle<T extends HTMLElement>(text: string, active: boolean) {
  const ref = useRef<T>(null);
  const twoLineHeight = useRef<number | undefined>(undefined);
  const [expanded, setExpanded] = useState(false);
  const [needed, setNeeded] = useState<boolean | undefined>(undefined);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!active || !element) return;
    const measure = () => {
      if (element.clientHeight === 0) {
        setNeeded(undefined);
      } else if (!expanded) {
        const hidden = element.scrollHeight - element.clientHeight > ROUNDING_PX;
        if (hidden) twoLineHeight.current = element.clientHeight;
        setNeeded(hidden);
      } else if (twoLineHeight.current === undefined) {
        setNeeded(undefined);
      } else {
        const taller = element.scrollHeight - twoLineHeight.current > ROUNDING_PX;
        setNeeded(taller);
        if (!taller) setExpanded(false);
      }
    };
    measure();
    return observeTextLayout(element, measure);
  }, [active, expanded, text]);
  return {
    ref,
    expanded,
    visible: needed !== false,
    flip: () => setExpanded((open) => !open),
  };
}
